import {
  type IComputedValue,
  action,
  computed,
  makeObservable,
  observable,
} from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import { inElectron } from '@/utils';

export type TabRoute = {
  path: string;
  search?: Record<string, unknown>;
};

export type Tab = {
  id: string;
  route: TabRoute;
  history: TabRoute[];
  historyIndex: number;
  lastAccessedAt: number;
  pinned?: boolean;
  feedViewId?: string;
};

type ClosedTabDiscardListener = (tab: Tab) => void;

const DEFAULT_TAB_PATH = '/chats';
const MAX_CLOSED_TABS = 10;
const MAX_HISTORY_SIZE = 50;

const DEFAULT_CLOSE_IDLE_TABS_AFTER_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

const generateTabId = (): string => crypto.randomUUID();

export const routesEqual = (a: TabRoute, b: TabRoute): boolean =>
  a.path === b.path &&
  JSON.stringify(a.search ?? {}) === JSON.stringify(b.search ?? {});

class TabStore {
  tabs: Tab[] = [];
  activeTabId: string | null = null;
  /** Null keeps idle tabs open */
  closeIdleTabsAfterDays: number | null = DEFAULT_CLOSE_IDLE_TABS_AFTER_DAYS;
  private closedTabs: Tab[] = [];
  private closedTabDiscardListeners = new Set<ClosedTabDiscardListener>();

  onClosedTabDiscarded(listener: ClosedTabDiscardListener): () => void {
    this.closedTabDiscardListeners.add(listener);
    return () => {
      this.closedTabDiscardListeners.delete(listener);
    };
  }

  private notifyClosedTabDiscarded(tab: Tab) {
    this.closedTabDiscardListeners.forEach((listener) => listener(tab));
  }

  constructor() {
    makeObservable(this, {
      tabs: observable,
      activeTabId: observable,
      closeIdleTabsAfterDays: observable,
      activeTab: computed,
      showTabBar: computed,
      canGoBack: computed,
      canGoForward: computed,
      createTab: action,
      closeTabs: action,
      reopenLastClosedTab: action,
      activateTab: action,
      setFeedViewId: action,
      setTabPinned: action,
      updateTabRoute: action,
      reorderTabs: action,
      goBack: action,
      goForward: action,
      setCloseIdleTabsAfterDays: action,
    });

    makePersistable(this, {
      name: 'tabStore',
      properties: ['tabs', 'activeTabId', 'closeIdleTabsAfterDays'],
      storage: window.localStorage,
    });
  }

  get activeTab(): Tab | undefined {
    return this.tabs.find((t) => t.id === this.activeTabId);
  }

  private readonly activeFlags = new Map<string, IComputedValue<boolean>>();

  // Per-tab computed so a switch only invalidates the two tabs whose result
  // changes, instead of every observer that read activeTabId
  isTabActive(tabId: string): boolean {
    let flag = this.activeFlags.get(tabId);
    if (!flag) {
      flag = computed(() => this.activeTabId === tabId);
      this.activeFlags.set(tabId, flag);
    }
    return flag.get();
  }

  get showTabBar(): boolean {
    return inElectron() && this.tabs.length > 0;
  }

  get canGoBack(): boolean {
    const tab = this.activeTab;
    return !!tab && !!tab.history && tab.historyIndex > 0;
  }

  get canGoForward(): boolean {
    const tab = this.activeTab;
    return !!tab && !!tab.history && tab.historyIndex < tab.history.length - 1;
  }

  private ensureTabHistory(tab: Tab) {
    if (!tab.history) {
      tab.history = [tab.route];
      tab.historyIndex = 0;
    }
  }

  private removeTab(tabId: string) {
    this.tabs = this.tabs.filter((t) => t.id !== tabId);
    this.activeFlags.delete(tabId);
  }

  createTab(route: TabRoute): Tab {
    const tab: Tab = {
      id: generateTabId(),
      route,
      history: [route],
      historyIndex: 0,
      lastAccessedAt: Date.now(),
      feedViewId: this.activeTab?.feedViewId,
    };
    this.tabs.push(tab);
    this.activeTabId = tab.id;
    return tab;
  }

  closeTabs(
    tabIds: string[],
    fallbackRoute: TabRoute = { path: DEFAULT_TAB_PATH }
  ): {
    closedTabs: Tab[];
    didActiveTabChange: boolean;
  } {
    const previousActiveTabId = this.activeTabId;
    const closedTabs = tabIds.flatMap(
      (tabId) => this.closeTab(tabId, fallbackRoute) ?? []
    );
    return {
      closedTabs,
      didActiveTabChange: this.activeTabId !== previousActiveTabId,
    };
  }

  private closeTab(tabId: string, fallbackRoute: TabRoute): Tab | undefined {
    const idx = this.tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return undefined;

    const closedTab = this.tabs[idx];
    const wasActive = tabId === this.activeTabId;
    this.removeTab(tabId);
    this.closedTabs.push(closedTab);
    if (this.closedTabs.length > MAX_CLOSED_TABS) {
      const discardedTab = this.closedTabs.shift();
      if (discardedTab) this.notifyClosedTabDiscarded(discardedTab);
    }

    if (this.tabs.length === 0) {
      this.createTab(fallbackRoute);
    } else if (wasActive) {
      const nextIdx = Math.min(idx, this.tabs.length - 1);
      this.activeTabId = this.tabs[nextIdx].id;
    }
    return closedTab;
  }

  reopenLastClosedTab(): Tab | undefined {
    const tab = this.closedTabs.pop();
    if (!tab) return undefined;

    tab.lastAccessedAt = Date.now();
    this.tabs.push(tab);
    if (tab.pinned) this.moveTabToPinnedBoundary(tab);
    this.activeTabId = tab.id;
    return tab;
  }

  activateTab(tabId: string) {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (!tab) return;
    tab.lastAccessedAt = Date.now();
    this.activeTabId = tabId;
  }

  setFeedViewId(tabId: string, feedViewId: string) {
    const tab = this.tabs.find((candidate) => candidate.id === tabId);
    if (tab) {
      tab.feedViewId = feedViewId;
    }
  }

  setCloseIdleTabsAfterDays(days: number | null) {
    this.closeIdleTabsAfterDays = days;
  }

  /** Unpinned background tabs not opened for longer than the idle setting */
  getIdleTabs(): Tab[] {
    if (this.closeIdleTabsAfterDays === null) return [];

    const oldestAllowedAccess =
      Date.now() - this.closeIdleTabsAfterDays * DAY_MS;
    return this.tabs.filter(
      (tab) =>
        !tab.pinned &&
        tab.id !== this.activeTabId &&
        tab.lastAccessedAt <= oldestAllowedAccess
    );
  }

  // Pinned tabs always sit before unpinned ones. The boundary index is both
  // the end of the pinned region and the start of the unpinned region, so a
  // tab that just changed pinned state moves there
  private moveTabToPinnedBoundary(tab: Tab) {
    const otherTabs = this.tabs.filter((t) => t.id !== tab.id);
    const pinnedCount = otherTabs.filter((t) => t.pinned).length;
    otherTabs.splice(pinnedCount, 0, tab);
    this.tabs = otherTabs;
  }

  setTabPinned(tabId: string, pinned: boolean) {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (!tab || !!tab.pinned === pinned) return;
    tab.pinned = pinned;
    this.moveTabToPinnedBoundary(tab);
  }

  updateTabRoute(tabId: string, route: TabRoute, { pushHistory = true } = {}) {
    const tab = this.tabs.find((candidate) => candidate.id === tabId);
    if (!tab) return;
    if (routesEqual(tab.route, route)) return;

    tab.route = route;
    tab.lastAccessedAt = Date.now();

    if (!pushHistory) return;

    this.ensureTabHistory(tab);
    tab.history = tab.history.slice(0, tab.historyIndex + 1);
    tab.history.push(route);
    tab.historyIndex = tab.history.length - 1;

    if (tab.history.length > MAX_HISTORY_SIZE) {
      const excess = tab.history.length - MAX_HISTORY_SIZE;
      tab.history = tab.history.slice(excess);
      tab.historyIndex -= excess;
    }
  }

  private navigateHistory(delta: -1 | 1): TabRoute | null {
    const tab = this.activeTab;
    if (!tab) return null;
    this.ensureTabHistory(tab);
    const nextIndex = tab.historyIndex + delta;
    if (nextIndex < 0 || nextIndex >= tab.history.length) return null;
    tab.historyIndex = nextIndex;
    tab.route = tab.history[nextIndex];
    return tab.route;
  }

  goBack(): TabRoute | null {
    return this.navigateHistory(-1);
  }

  goForward(): TabRoute | null {
    return this.navigateHistory(1);
  }

  reorderTabs(fromIndex: number, toIndex: number) {
    const newTabs = [...this.tabs];
    const [moved] = newTabs.splice(fromIndex, 1);
    if (!moved) return;
    const pinnedCount = newTabs.filter((t) => t.pinned).length;
    const boundedIndex = moved.pinned
      ? Math.min(toIndex, pinnedCount)
      : Math.max(toIndex, pinnedCount);
    newTabs.splice(boundedIndex, 0, moved);
    this.tabs = newTabs;
  }

  getTabByIndex(index: number): Tab | undefined {
    return this.tabs[index];
  }
}

export const tabStore = new TabStore();
